import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useAccountAmount } from "@shared/features/onboarding/hooks";
import { AccountData } from "@shared/features/onboarding/types";
import { GroupData } from "@shared/types";
import {
  CONTRACTS_EXCHANGES,
  DISPLAY_CURRENCIES,
  EXCHANGES,
  MARGIN_EXCHANGES,
} from "@shared/common/constants";
import useAccountMainCoins from "@shared/features/onboarding/hooks/useAccountMainCoins";
import { GroupOverviewContext } from "@shared/features/onboarding/GroupOverview.context";
import {
  MAX_ACCOUNTS_ROWS,
  MAX_GROUP_CONFIGURATIONS,
} from "@shared/features/onboarding/constants";
import { changeSelectedItemPosition } from "@shared/features/onboarding/utils";
import { useClientsData, useSymbolsMetadata } from "@shared/utils/hooks/misc";
import useGroupMainCoinsSignalsCount from "@shared/features/bots/BotForm/useGroupMainCoinsSignalsCount";

const useAccountsList = (
  groupData?: GroupData,
  symbol?: string,
  isBot?: boolean,
  preferredExchange?: string | null,
  preferredAccountId?: number,
  isSignalsOnboarding = false
) => {
  const { selectedAccountId, setSelectedAccountId, accounts, setAccounts } =
    useContext(GroupOverviewContext)!;
  const [isAccountsModalVisible, setIsAccountsModalVisible] = useState(false);
  const [isAccountChosenByUser, setIsAccountChosenByUser] = useState(false);
  const isSelectedWithExactSignals = useRef(false);

  const {
    activeClientIds,
    activeClientsData,
    getAccountExchangeById,
    getAccountById,
  } = useClientsData();
  const {
    getAccountAmountByCurrency,
    accountTotalAndAvailableAmountQuery,
    getAccountMainCoinData,
  } = useAccountAmount();
  const { isSymbolActiveInAccount, getMainCoin } = useSymbolsMetadata();
  const {
    mainCoinsToSignalsCount,
    hasExactSignalsCount,
    isError: isSignalsCountError,
  } = useGroupMainCoinsSignalsCount({});
  const { getTotalLastMonthSignals } = useAccountMainCoins(
    mainCoinsToSignalsCount
  );

  const accountsData: AccountData[] = useMemo(() => {
    if ((!isBot && !groupData) || !accountTotalAndAvailableAmountQuery?.data)
      return [];
    return activeClientIds.map((id) => {
      const exchange = getAccountExchangeById(id);
      return {
        id: id,
        exchange: exchange,
        total: getAccountAmountByCurrency(id, DISPLAY_CURRENCIES.USD, "total"),
        available: getAccountAmountByCurrency(
          id,
          DISPLAY_CURRENCIES.USD,
          "available"
        ),
        signals: exchange ? getTotalLastMonthSignals(exchange) : 0,
        accountName: getAccountById(id)?.client_name,
      };
    });
  }, [
    activeClientIds,
    activeClientsData,
    accountTotalAndAvailableAmountQuery?.data,
    mainCoinsToSignalsCount,
  ]);

  const isRadioDisabled = (account: AccountData) =>
    !!symbol && !!account.id && !isSymbolActiveInAccount(symbol, account.id);

  const sortedAccountsData = useMemo(() => {
    const parsedAccountsData = [...accountsData];
    if (accountTotalAndAvailableAmountQuery.isSuccess)
      return parsedAccountsData.sort(
        (a, b) =>
          +isRadioDisabled(a) - +isRadioDisabled(b) ||
          +(b.id === preferredAccountId) - +(a.id === preferredAccountId) ||
          +(b.exchange === preferredExchange) -
            +(a.exchange === preferredExchange) ||
          b.signals - a.signals ||
          (b.total || 0) - (a.total || 0)
      );
  }, [
    accountsData,
    preferredExchange,
    preferredAccountId,
    symbol,
    isSymbolActiveInAccount,
  ]);

  const getDefaultAccount = () => {
    if (isBot) {
      if (preferredExchange === null) return undefined;
      const compatibleAccounts = sortedAccountsData?.filter(
        (account) =>
          symbol && account.id && isSymbolActiveInAccount(symbol, account.id)
      );
      return (
        compatibleAccounts?.find(
          (account) => account.exchange === preferredExchange
        ) ??
        compatibleAccounts?.find(
          (account) =>
            !preferredExchange ||
            (!!account.exchange &&
              (account.exchange === EXCHANGES.BYBIT_SPOT_MARGIN) ===
                (preferredExchange === EXCHANGES.BYBIT_SPOT_MARGIN) &&
              MARGIN_EXCHANGES.has(account.exchange) ===
                MARGIN_EXCHANGES.has(preferredExchange) &&
              CONTRACTS_EXCHANGES.has(account.exchange) ===
                CONTRACTS_EXCHANGES.has(preferredExchange))
        )
      );
    }
    return sortedAccountsData?.[0];
  };

  const isSingleAccount =
    accountsData.length === 1 &&
    (preferredExchange === undefined ||
      accountsData[0].exchange === preferredExchange ||
      (isBot && accountsData[0].id === getDefaultAccount()?.id));
  const showMore = accountsData.length > MAX_ACCOUNTS_ROWS;
  const coin = getMainCoin(symbol, getDefaultAccount()?.exchange);

  const isWaitingForSignals =
    isSignalsOnboarding && !mainCoinsToSignalsCount && !isSignalsCountError;

  useEffect(() => {
    if (isWaitingForSignals) return;
    const isSelectionFinal =
      isSignalsOnboarding ||
      isAccountChosenByUser ||
      isSelectedWithExactSignals.current ||
      !hasExactSignalsCount;
    if (selectedAccountId && isSelectionFinal) return;
    const defaultAccountId = getDefaultAccount()?.id;
    if (defaultAccountId) {
      isSelectedWithExactSignals.current = hasExactSignalsCount;
      setSelectedAccountId(defaultAccountId);
    }
  }, [
    isWaitingForSignals,
    isSignalsOnboarding,
    hasExactSignalsCount,
    isAccountChosenByUser,
    sortedAccountsData,
    selectedAccountId,
    symbol,
    isBot,
    preferredExchange,
    isSymbolActiveInAccount,
  ]);

  const accountsListData = {
    value: selectedAccountId,
    onChange: (value: number) => {
      setIsAccountChosenByUser(true);
      setSelectedAccountId(value);
    },
    style: { width: "100%" },
    options: sortedAccountsData,
  };

  useEffect(() => {
    if (accountsListData?.options?.length) {
      setAccounts(accountsListData?.options);
    }
  }, [accountsListData.options]);

  const spans = isBot ? [12, 12] : [11, 5, 8];
  const isRadio = !isSingleAccount;

  const getAccountRowData = (account: AccountData) => {
    const disabled = isRadioDisabled(account);
    const coinData = coin
      ? getAccountMainCoinData(account.id, coin)
      : undefined;
    const total = isBot ? coinData?.total : account.total;
    const available = isBot ? coinData?.available : account.available;
    const showMainCoins =
      !isBot && account.signals !== 0 && account.id === selectedAccountId;

    return {
      disabled,
      total,
      available,
      showMainCoins,
    };
  };

  const getTooltipErrorMessage = (account: AccountData) =>
    `${symbol} is not available on ${account.exchange}`;

  const closeAccountsListModal = () => setIsAccountsModalVisible(false);

  const onClickUseSelectedAccount = () => {
    closeAccountsListModal();
    if (accounts) {
      const updatedAccounts = changeSelectedItemPosition(
        accounts,
        selectedAccountId,
        MAX_GROUP_CONFIGURATIONS
      );
      setAccounts(updatedAccounts);
    }
  };

  return {
    selectedAccountId,
    sortedAccountsData,
    isSingleAccount,
    isRadioDisabled,
    accountsListData,
    coin,
    spans,
    isRadio,
    getAccountRowData,
    getTooltipErrorMessage,
    showMore,
    isAccountsModalVisible,
    setIsAccountsModalVisible,
    closeAccountsListModal,
    onClickUseSelectedAccount,
    accounts,
  };
};

export default useAccountsList;
